import { DRAND_API, DRAND_GENESIS, DRAND_PERIOD, ROUND_MARGIN } from './constants.js';
/**
 * drand quicknet, the beacon the arena verifies on chain. The round math here is
 * `Quicknet.roundAfter` and `Quicknet.publishTime` exactly; the contract decides
 * which round settles a period, and this only predicts it so the keeper knows
 * when to fetch and the site knows what to show.
 */
/** `Quicknet.roundAfter`: the first round published at or after `timestamp`, plus the margin. */
export function roundAfter(timestamp, margin = ROUND_MARGIN) {
    if (timestamp <= DRAND_GENESIS)
        return 1n + margin;
    const elapsed = timestamp - DRAND_GENESIS;
    return (elapsed + DRAND_PERIOD - 1n) / DRAND_PERIOD + 1n + margin;
}
/** `Quicknet.publishTime`: the unix second at which `round` is published. */
export function publishTime(round) {
    return DRAND_GENESIS + DRAND_PERIOD * (round - 1n);
}
/** The round quicknet has most recently published at `now`. */
export function latestRound(now = BigInt(Math.floor(Date.now() / 1000))) {
    if (now < DRAND_GENESIS)
        return 0n;
    return (now - DRAND_GENESIS) / DRAND_PERIOD + 1n;
}
export class BeaconUnavailable extends Error {
    round;
    status;
    constructor(round, status) {
        super(`quicknet round ${round} is not available yet (HTTP ${status})`);
        this.round = round;
        this.status = status;
        this.name = 'BeaconUnavailable';
    }
}
/**
 * Reads one round from the beacon's public interface. Anyone may do this; the
 * signature is the same for everyone and is verified on chain, so a hostile
 * response cannot do more than fail the pairing check.
 */
export async function fetchBeacon(round, { api = DRAND_API, signal } = {}) {
    const res = await fetch(`${api}/rounds/${round}`, { signal });
    if (!res.ok)
        throw new BeaconUnavailable(round, res.status);
    const body = (await res.json());
    const sig = body.signature?.replace(/^0x/, '');
    if (!sig || !/^[0-9a-fA-F]{96}$/.test(sig)) {
        throw new Error(`quicknet round ${round} returned a signature that is not 48 bytes`);
    }
    if (body.round !== undefined && BigInt(body.round) !== round) {
        throw new Error(`asked quicknet for round ${round} and got ${body.round}`);
    }
    return { round, signature: `0x${sig}` };
}
//# sourceMappingURL=drand.js.map